'use client';
import React, { useState, useRef, MouseEvent } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import SeoHeading from '../seo/SeoHeading';
import { HomeSeoSlots, getHomeSlot } from '../../../src/lib/seo/home-headings';

type Props = {
  headings: HomeSeoSlots;
};

const HotelsGrid: React.FC<Props> = ({ headings }) => {
  const router = useRouter();
  const sliderRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const [isDown, setIsDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const isDragging = useRef(false);

  const hotel1Title = getHomeSlot(headings, 'hotelTitle1');
  const hotel2Title = getHomeSlot(headings, 'hotelTitle2');
  const hotel3Title = getHomeSlot(headings, 'hotelTitle3');

  const hotels = [
    {
      id: 1,
      city: 'تهران',
      title: hotel1Title.text,
      subtitle: 'ارزان‌ترین قیمت رزرو هتل در تهران',
      image: 'images/city/tehran.jpg',
      link: '/accommodation/THR?rooms=[{"adults"%3A1%2C"children"%3A[]}]',
    },
    {
      id: 2,
      city: 'اصفهان',
      title: hotel2Title.text,
      subtitle: 'ارزان‌ترین قیمت رزرو هتل در اصفهان',
      image: 'images/city/esfahan.jpg',
      link: '/accommodation/IFN?rooms=[{"adults"%3A1%2C"children"%3A[]}]',
    },
    {
      id: 3,
      city: 'اهواز',
      title: hotel3Title.text,
      subtitle: 'ارزان‌ترین قیمت رزرو هتل در اهواز',
      image: 'images/city/ahvaz.jpg',
      link: '/accommodation/AWZ?rooms=[{"adults"%3A1%2C"children"%3A[]}]',
    },
  ];

  const sectionTitle = getHomeSlot(headings, 'hotelsTitle');

  const handleScroll = () => {
    if (!sliderRef.current) return;
    const value = sliderRef.current.scrollLeft;
    const width = sliderRef.current.offsetWidth;
    const index = Math.round(Math.abs(value) / (width * 0.8));
    setActiveIndex(Math.min(index, hotels.length - 1));
  };

  const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    if (!sliderRef.current) return;
    setIsDown(true);
    isDragging.current = false;

    sliderRef.current.classList.add('active-grab');
    setStartX(e.pageX - sliderRef.current.offsetLeft);
    setScrollLeft(sliderRef.current.scrollLeft);
  };

  const handleMouseUpOrLeave = () => {
    setIsDown(false);
    if (sliderRef.current) {
      sliderRef.current.classList.remove('active-grab');
    }
  };

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!isDown || !sliderRef.current) return;
    e.preventDefault();
    const x = e.pageX - sliderRef.current.offsetLeft;
    const walk = (x - startX) * 2;

    if (Math.abs(walk) > 5) {
      isDragging.current = true;
    }

    sliderRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleCardClick = (link: string) => {
    if (!isDragging.current) {
      router.push(link);
    }
  };

  return (
    <section className="hotels-section">
      <div className="container">
        <SeoHeading slot={sectionTitle} className="hotels-grid-section-title" />

        <div
          className="gallery-grid"
          ref={sliderRef}
          onScroll={handleScroll}
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseUpOrLeave}
          onMouseUp={handleMouseUpOrLeave}
          onMouseMove={handleMouseMove}
        >
          {hotels.map((hotel) => (
            <div
              key={hotel.id}
              className="card"
              onDragStart={(e) => e.preventDefault()}
              onClick={() => handleCardClick(hotel.link)}
            >
              <Image
                src={`/${hotel.image.replace(/^\//, '')}`}
                alt={hotel.title}
                fill
                sizes="(max-width: 768px) 100vw, 55vw"
                className="card-image"
              />
              <div className="gradient-overlay">
                <div className="text-content">
                  <h3 className="city-title">{hotel.title}</h3>
                  <p className="city-subtitle">{hotel.subtitle}</p>
                </div>
              </div>
            </div>
          ))}

          <div className="button-wrapper">
            <button
              className="action-button"
              onClick={() =>
                router.push('/accommodation/hotels?rooms=[{"adults"%3A1%2C"children"%3A[]}]')
              }
            >
              دیدن همه هتل‌ها
            </button>
          </div>
        </div>

        <div className="dots-container">
          {hotels.map((_, index) => (
            <span
              key={index}
              className={`dot ${activeIndex === index ? 'active' : ''}`}
              onClick={() => {
                if (sliderRef.current) {
                  const cardWidth = sliderRef.current.offsetWidth * 0.85 + 15;
                  sliderRef.current.scrollTo({
                    left: -(index * cardWidth),
                    behavior: 'smooth',
                  });
                }
              }}
            ></span>
          ))}
        </div>
      </div>

      <style jsx>{`
        .hotels-section {
          width: 100%;
          padding: 60px 20px;
          direction: rtl;
        }

        .container {
          max-width: 1250px;
          margin: 0 auto;
        }

        :global(.hotels-grid-section-title) {
          text-align: right;
          font-size: 28px;
          font-weight: 800;
          color: #374151;
          margin: 0 0 30px 0;
          display: block;
        }

        .gallery-grid {
          display: grid;
          grid-template-columns: 1.6fr 1fr;
          grid-template-rows: 220px 220px;
          gap: 24px;
          grid-template-areas:
            'big small1'
            'big small2';
          overflow: hidden;
          cursor: default;
        }

        .gallery-grid.active-grab {
          cursor: grabbing;
          cursor: -webkit-grabbing;
        }

        .card:nth-child(1) {
          grid-area: big;
        }

        .card:nth-child(2) {
          grid-area: small1;
        }

        .card:nth-child(3) {
          grid-area: small2;
        }

        .button-wrapper {
          grid-column: 2;
          display: flex;
          justify-content: flex-end;
          align-items: flex-start;
          margin-top: -10px;
        }

        /* استایل کارت بدون تغییر ابعاد یا انیمیشن‌های سنگین */
        .card {
          position: relative;
          border-radius: 24px;
          overflow: hidden;
          width: 100%;
          height: 100%;
          cursor: pointer;
          user-select: none;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
          border: 2px solid transparent;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }

        .card:hover {
          border-color: rgba(255, 255, 255, 0.4);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
        }

        .card-image {
          object-fit: cover;
          pointer-events: none;
        }

        .gradient-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to top,
            rgba(0, 0, 0, 0.85) 0%,
            rgba(0, 0, 0, 0.35) 45%,
            rgba(0, 0, 0, 0.05) 100%
          );
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          align-items: center;
          padding-bottom: 25px;
          pointer-events: none;
          transition: background 0.2s ease;
        }

        .card:hover .gradient-overlay {
          background: linear-gradient(
            to top,
            rgba(0, 0, 0, 0.92) 0%,
            rgba(0, 0, 0, 0.45) 50%,
            rgba(0, 0, 0, 0.1) 100%
          );
        }

        .text-content {
          text-align: center;
          padding: 0 15px;
          pointer-events: none;
        }

        .city-title {
          color: #ffffff;
          font-size: 24px;
          font-weight: 800;
          text-align: center;
          margin: 0 0 6px 0;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.6);
          transition: color 0.2s ease;
        }

        .card:hover .city-title {
          color: #facc15; /* روشن‌تر شدن رنگ عنوان به زرد ملایم */
        }

        .city-subtitle {
          color: #e5e7eb;
          font-size: 14px;
          font-weight: 500;
          text-align: center;
          margin: 0;
          opacity: 0.85;
          transition: opacity 0.2s ease, color 0.2s ease;
        }

        .card:hover .city-subtitle {
          opacity: 1;
          color: #ffffff;
        }

        .action-button {
          background-color: #65a30d;
          color: white;
          border: none;
          border-radius: 10px;
          padding: 12px 35px;
          font-family: inherit;
          font-weight: 700;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(101, 163, 13, 0.3);
          transition: background-color 0.2s ease;
        }

        .action-button:hover {
          background-color: #52840a;
        }

        .dots-container {
          display: none;
        }

        @media (max-width: 900px) {
          :global(.hotels-grid-section-title) {
            text-align: center;
            font-size: 24px;
            margin-bottom: 25px;
          }

          .gallery-grid {
            display: flex;
            gap: 15px;
            overflow-x: auto;
            padding-bottom: 20px;
            padding-right: 20px;
            padding-left: 20px;
            cursor: grab;
            scrollbar-width: none;
            -ms-overflow-style: none;
            scroll-snap-type: x mandatory;
            scroll-behavior: smooth;
          }

          .gallery-grid::-webkit-scrollbar {
            display: none;
          }

          .gallery-grid.active-grab {
            scroll-snap-type: none;
            scroll-behavior: auto;
            cursor: grabbing;
          }

          .card {
            min-width: 85%;
            height: 380px;
            scroll-snap-align: center;
            border-radius: 24px;
          }

          .city-title {
            font-size: 26px;
          }

          .button-wrapper {
            display: none;
          }

          .dots-container {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 6px;
            margin-top: 5px;
          }

          .dot {
            width: 8px;
            height: 8px;
            background-color: #9ca3af;
            border-radius: 50%;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            cursor: pointer;
          }

          .dot.active {
            background-color: #1e40af;
            width: 28px;
            border-radius: 10px;
          }
        }
      `}</style>
    </section>
  );
};

export default HotelsGrid;
